import { parse, stringify } from 'qs';
import { hasFeatureInLocalStorage } from '../../../global/featureFlags';

// Teams (v3): single source of truth for the sticky team-view dropdown (D6).
// The dropdown persists its value per page in localStorage and drives a `team`
// URL query param (a team slug, 'unassigned', or absent = All teams).
export const INBOX_TEAM_VIEW_STORAGE_KEY = 'm8_inbox_team_view';
export const REFERRAL_TEAM_VIEW_STORAGE_KEY = 'm8_referral_team_view';
export const TEAM_VIEW_ALL = 'all';
export const TEAM_VIEW_UNASSIGNED = 'unassigned';

const parseOptions = { ignoreQueryPrefix: true };
const stringifyOptions = {
  format: 'RFC1738',
  addQueryPrefix: true,
  arrayFormat: 'brackets',
  encode: true,
  sort: (a, b) => a.localeCompare(b),
};

// Read the stored sticky value, tolerating environments without localStorage.
export const readStickyTeam = (storageKey) => {
  try {
    return localStorage.getItem(storageKey);
  } catch {
    return null;
  }
};

// A stored value is meaningful only when it is a specific team or 'unassigned';
// 'all' (or empty) means "no team filter" and needs no restoration.
export const hasStickyTeamValue = (value) => !!value && value !== TEAM_VIEW_ALL;

// Normalize a raw team-view dropdown selection into the list of values to
// persist/query. The dropdown is multi-select: "No teams" (unassigned) CAN be
// combined with specific team slugs — the backend ORs the untagged scope with the
// selected-team scope (TeamVisibility#team) — so every selected value is kept.
// Do NOT reintroduce a mutual-exclusion rule between the sentinel and slugs.
export const normalizeTeamViewSelection = (rawValue) => (Array.isArray(rawValue) ? rawValue : [rawValue]).filter(Boolean);

// The analytics view CATEGORY for a normalized selection (never a slug/name):
// 'all' (empty), 'unassigned' (only the sentinel), or 'team' (any named team,
// including a combined sentinel + team selection).
export const teamViewCategory = (values) => {
  if (values.length === 0) return TEAM_VIEW_ALL;
  if (values.length === 1 && values[0] === TEAM_VIEW_UNASSIGNED) return TEAM_VIEW_UNASSIGNED;
  return 'team';
};

// Scalar for a single value (team=<slug>), array for several (team[]=a&team[]=b) —
// the backend team filter accepts either form.
export const teamViewQueryValue = (values) => (values.length === 1 ? values[0] : values);

// Whether a team-list fetch succeeded, i.e. is safe to reconcile the URL/sticky
// team selection against. The `getOrganizationTeams` crud action RESOLVES (never
// rejects) to `{ error: true }` on an HTTP failure and `{ canceled: true }` on an
// aborted request; a successful load resolves to the axios response (no such
// flag), and a test/no-op stub resolves to undefined. Reconciliation must run
// ONLY after a successful load — an empty/stale Redux team list from a failed
// load would otherwise be treated as authoritative, strip a valid team from the
// URL, flip the picker to "All teams", and silently broaden the visible list.
// Fail closed: any explicit failure/cancel flag means "not ready to reconcile".
export const isSuccessfulTeamsFetch = (result) => !(result && (result.error || result.canceled));

// Seed the stored sticky team-view value into the initial records query so the
// very first fetch on mount/navigate-back already carries team=<slug|unassigned>
// instead of firing a team-less fetch that the async sticky-restore has to race.
//
// Rules:
//   - Only when the teams flag is cached-enabled, so flag-off (non-teams) orgs
//     are never touched and their query is unchanged. When the flag is not yet
//     cached the filter bar re-drives the query once the flag resolves.
//   - A `team` already in the URL wins (deep link) — never overridden.
//   - A stored 'all'/empty value is a no-op.
//   - A stored slug that no longer maps to a live team (deleted) cannot be
//     validated here (teams aren't loaded yet); the filter bar validates it
//     against the fetched teams list and falls back to All if needed.
//   - ENG-3551: with saved views enabled for the page, a URL that selects a
//     saved view (`saved_view`) is the view's exact filters; the sticky team is
//     never injected into it. Callers without the option are unchanged.
//
// Returns the (possibly rewritten) search string.
export const resolveStickyTeamQuery = (search, storageKey, { savedViewsEnabled = false } = {}) => {
  if (hasFeatureInLocalStorage('teams') !== true) return search;

  const params = parse(search || '', parseOptions);
  if (params.team) return search;
  if (savedViewsEnabled === true && params.saved_view) return search;

  const saved = readStickyTeam(storageKey);
  if (!hasStickyTeamValue(saved)) return search;

  // The sticky value can hold multiple team slugs (comma-joined) now that the
  // team-view filter is multi-select. Emit a scalar for a single value (keeps
  // the URL as team=<slug>) and an array for several (team[]=a&team[]=b); the
  // backend team filter accepts either form.
  const values = String(saved).split(',').map((value) => value.trim()).filter(Boolean);
  if (values.length === 0) return search;

  params.team = values.length === 1 ? values[0] : values;
  return stringify(params, stringifyOptions);
};
